import React from 'react'
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@the-platform-group/component-library'
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@the-platform-group/component-library/Table'
import { getJobTitleAndOrgName, getJobFieldValue } from 'pages/MergeProfiles/utils'
import { JOB_CORE_FIELDS } from 'pages/MergeProfiles/constants'

/**
 * JobOverrideTable - Interactive table for selecting field overrides when mapping jobs
 * This table displays all available values for each field from mapped Platform jobs and the FactSet job, allowing the user to choose which value should be used in the final merge.
 * @param {Object} props
 * @param {Array<Object>} props.platformJobs - Array of Platform jobs being mapped
 * @param {Object} props.factSetJob - The FactSet job being mapped to
 * @param {Object} props.overrideValues - Current selections { fieldKey: jobId or 'factset' }
 * @param {Function} props.onOverrideChange - Callback when selection changes (fieldKey, value)
 *
 * @returns {React.Component} An interactive table with radio buttons for field selection
 */
const JobOverrideTable = ({ platformJobs, factSetJob, overrideValues, onOverrideChange }) => {
  return (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'background.secondary.default' }}>
            {/* Empty header for field labels */}
            <TableHeaderCell
              sx={{
                borderBottom: '1px solid',
                borderColor: 'border.interactive',
                minWidth: 100,
                verticalAlign: 'top',
              }}
            />

            {platformJobs.map(job => (
              <TableHeaderCell
                key={job.id}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'border.interactive',
                  minWidth: 150,
                  maxWidth: 250,
                  verticalAlign: 'top',
                  textAlign: 'left',
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Platform Job
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {getJobTitleAndOrgName(job)}
                </Typography>
              </TableHeaderCell>
            ))}

            <TableHeaderCell
              sx={{
                borderBottom: '1px solid',
                borderColor: 'border.interactive',
                minWidth: 150,
                maxWidth: 250,
                verticalAlign: 'top',
                textAlign: 'left',
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                FactSet Job
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {getJobTitleAndOrgName(factSetJob)}
              </Typography>
            </TableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {JOB_CORE_FIELDS.map(field => (
            <TableRow
              key={field.key}
              sx={{
                '&:last-child td': { borderBottom: 'none' },
              }}
            >
              {/* Field Label Cell */}
              <TableCell
                sx={{
                  backgroundColor: 'background.secondary.default',
                  borderBottom: '1px solid',
                  borderColor: 'border.secondary',
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {field.label}
                </Typography>
              </TableCell>

              {/* Radio Group for all value options */}
              <RadioGroup
                row
                value={overrideValues[field.key] || 'factset'}
                onChange={e => onOverrideChange(field.key, e.target.value)}
                sx={{ display: 'contents' }}
              >
                {platformJobs.map(job => {
                  const value = getJobFieldValue(job, field.key, { defaultValue: null })
                  return (
                    <TableCell
                      key={job.id}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'border.secondary',
                        maxWidth: 250,
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        p: 1,
                      }}
                    >
                      {value ? (
                        <FormControlLabel
                          value={job.id}
                          control={<Radio size="small" sx={{ p: 0.5 }} />}
                          label={
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {value}
                            </Typography>
                          }
                          sx={{ m: 0, alignItems: 'center' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )
                })}

                {/* FactSet Job Column */}
                <TableCell
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'border.secondary',
                    maxWidth: 250,
                    verticalAlign: 'middle',
                    textAlign: 'left',
                    p: 1,
                  }}
                >
                  <FormControlLabel
                    value="factset"
                    control={<Radio size="small" sx={{ p: 0.5 }} />}
                    label={
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {getJobFieldValue(factSetJob, field.key, { defaultValue: null }) || '-'}
                      </Typography>
                    }
                    sx={{ m: 0, alignItems: 'center' }}
                  />
                </TableCell>
              </RadioGroup>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default JobOverrideTable
